import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DeletionPreview {
  is_archived: boolean;
  can_delete: boolean;
  records_to_delete: {
    device_assignments: number;
    device_assignment_credits: number;
    email_subscriptions: number;
    system_notifications: number;
    outage_notification_views: number;
    organization_profile_notifications: number;
    user_roles: number;
    user_invitations_received: number;
    profile: number;
  };
  records_to_nullify: {
    orders: number;
    device_credit_packages: number;
    device_assignment_audit: number;
    device_groups: number;
    user_invitations_sent: number;
  };
  error?: string;
}

export const usePreviewUserDeletion = (userId: string | null) => {
  return useQuery({
    queryKey: ["deletion-preview", userId],
    queryFn: async (): Promise<DeletionPreview | null> => {
      if (!userId) return null;
      
      const { data, error } = await supabase.rpc('preview_user_data_deletion', {
        target_user_id: userId
      });
      
      if (error) {
        console.error('Error previewing deletion:', error);
        throw error;
      }
      
      return data as unknown as DeletionPreview;
    },
    enabled: !!userId,
  });
};

export const usePermanentlyDeleteUserData = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data, error } = await supabase.rpc('permanently_delete_user_data', {
        target_user_id: userId
      });
      
      if (error) {
        throw error;
      }
      
      return data;
    },
    onSuccess: () => {
      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["user-roles"] });
      queryClient.invalidateQueries({ queryKey: ["organization-contacts"] });
      toast.success("User data permanently deleted");
    },
    onError: (error: Error) => {
      console.error('Error deleting user data:', error);
      toast.error(`Failed to delete user data: ${error.message}`);
    }
  });
};
